import React, { useMemo, useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react";
import "./CanchasParrilla.css";

// Generar franjas horarias de 30 minutos desde 06:00 hasta 23:30
const TIME_SLOTS = [];
for (let hour = 6; hour <= 23; hour++) {
  const hStr = String(hour).padStart(2, "0");
  TIME_SLOTS.push(`${hStr}:00`);
  TIME_SLOTS.push(`${hStr}:30`);
}

const timeToMinutes = (timeStr) => {
  if (!timeStr) return 0;
  const [h, m] = timeStr.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

const minutesToTimeStr = (totalMinutes) => {
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const START_MINUTES = 6 * 60; // 06:00 = 360
const END_MINUTES = 24 * 60;  // 24:00 = 1440
const ROW_HEIGHT = 44; // Altura en px por cada slot de 30 mins
const PIXELS_PER_MINUTE = (ROW_HEIGHT * 2) / 60; // 1.4667 px / min

export default function CanchasParrilla({
  selectedDate,
  canchas = [],
  partidas = [],
  bloqueos = [],
  sucursalSeleccionada,
  onSlotClick,
  onEventClick,
  onBlockClick,
}) {
  // Estado para el arrastre de selección de tiempo
  const [isDragging, setIsDragging] = useState(false);
  const [dragCourt, setDragCourt] = useState(null); // Cancha objeto
  const [dragStartSlot, setDragStartSlot] = useState(null); // ej "18:00"
  const [dragCurrentSlot, setDragCurrentSlot] = useState(null); // ej "19:30"
  const dragMovedRef = useRef(false);

  const dateStr = useMemo(() => {
    if (!selectedDate) return "";
    const y = selectedDate.getFullYear();
    const m = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const d = String(selectedDate.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, [selectedDate]);

  // Canchas a mostrar: si hay sucursal seleccionada, solo de esa sucursal
  const canchasDisponibles = useMemo(() => {
    if (!sucursalSeleccionada || sucursalSeleccionada.value === -1) {
      return canchas;
    }
    return canchas.filter((c) => c.branchId === sucursalSeleccionada.value);
  }, [canchas, sucursalSeleccionada]);

  // Filtrar partidas del día actual
  const partidasDelDia = useMemo(() => {
    if (!dateStr) return [];
    return partidas
      .filter((p) => p.status !== "cancelled")
      .filter((p) => {
        if (!p.fecha) return false;
        const pDate = typeof p.fecha === "string" ? p.fecha.split("T")[0] : "";
        if (pDate !== dateStr) return false;
        if (sucursalSeleccionada && sucursalSeleccionada.value !== -1) {
          return p.branchId === sucursalSeleccionada.value;
        }
        return true;
      });
  }, [partidas, dateStr, sucursalSeleccionada]);

  // Filtrar bloqueos del día actual
  const bloqueosDelDia = useMemo(() => {
    if (!dateStr) return [];
    return bloqueos.filter((b) => {
      let bDate = b.fecha;
      if (typeof bDate === "string") bDate = bDate.split("T")[0];
      if (bDate !== dateStr) return false;
      if (sucursalSeleccionada && sucursalSeleccionada.value !== -1) {
        if (b.branchId && b.branchId !== sucursalSeleccionada.value) return false;
      }
      return true;
    });
  }, [bloqueos, dateStr, sucursalSeleccionada]);

  // Agrupar partidas por cancha
  const partidasPorCancha = useMemo(() => {
    const map = {};
    for (const p of partidasDelDia) {
      const cId = p.courtId;
      if (!cId) continue;
      if (!map[cId]) map[cId] = [];
      map[cId].push(p);
    }
    return map;
  }, [partidasDelDia]);

  // Agrupar bloqueos por cancha (los bloqueos globales o sin courtId se asignan a todas)
  const bloqueosPorCancha = useMemo(() => {
    const map = {};
    for (const b of bloqueosDelDia) {
      if (b.courtId) {
        if (!map[b.courtId]) map[b.courtId] = [];
        map[b.courtId].push(b);
      } else {
        // Bloqueo global: aplica a todas las canchas
        canchasDisponibles.forEach((c) => {
          if (!map[c.id]) map[c.id] = [];
          map[c.id].push(b);
        });
      }
    }
    return map;
  }, [bloqueosDelDia, canchasDisponibles]);

  const formatTimeOnly = (tStr) => {
    if (!tStr) return "";
    const match = tStr.match(/(\d{1,2}):(\d{2})/);
    return match ? `${match[1].padStart(2, "0")}:${match[2]}` : tStr;
  };

  // Cálculo del rango de selección durante el arrastre
  // Requerimiento: Mínimo 90 minutos, y de ahí pasos de 30 en 30 minutos
  const currentDragRange = useMemo(() => {
    if (!isDragging || !dragCourt || !dragStartSlot || !dragCurrentSlot) {
      return null;
    }
    const startMin = timeToMinutes(dragStartSlot);
    const currMin = timeToMinutes(dragCurrentSlot);

    let effectiveStart = Math.min(startMin, currMin);
    let rawEnd = Math.max(startMin, currMin) + 30; // Termina al final del slot tocado

    // Garantizar mínimo 90 minutos
    let rawDuration = rawEnd - effectiveStart;
    let finalDuration = Math.max(90, Math.ceil(rawDuration / 30) * 30);
    let finalEndMin = Math.min(END_MINUTES, effectiveStart + finalDuration);

    const horaInicio = minutesToTimeStr(effectiveStart);
    const horaFin = minutesToTimeStr(finalEndMin);

    const topPx = (effectiveStart - START_MINUTES) * PIXELS_PER_MINUTE;
    const heightPx = (finalEndMin - effectiveStart) * PIXELS_PER_MINUTE - 2;

    return {
      canchaId: dragCourt.id,
      horaInicio,
      horaFin,
      duracionMinutos: finalEndMin - effectiveStart,
      topPx,
      heightPx,
    };
  }, [isDragging, dragCourt, dragStartSlot, dragCurrentSlot]);

  // Manejar mouse down en celda vacía
  const handleCellMouseDown = (cancha, slot, e) => {
    // Solo clic izquierdo
    if (e.button !== 0) return;
    setIsDragging(true);
    setDragCourt(cancha);
    setDragStartSlot(slot);
    setDragCurrentSlot(slot);
    dragMovedRef.current = false;
  };

  // Manejar mouse enter en celda mientras se arrastra
  const handleCellMouseEnter = (cancha, slot) => {
    if (!isDragging || !dragCourt) return;
    if (dragCourt.id === cancha.id) {
      if (slot !== dragStartSlot) {
        dragMovedRef.current = true;
      }
      setDragCurrentSlot(slot);
    }
  };

  // Finalizar arrastre (mouseup)
  const finishDragSelection = () => {
    if (!isDragging || !dragCourt || !dragStartSlot) {
      setIsDragging(false);
      setDragCourt(null);
      setDragStartSlot(null);
      setDragCurrentSlot(null);
      return;
    }

    const startMin = timeToMinutes(dragStartSlot);
    const currMin = timeToMinutes(dragCurrentSlot || dragStartSlot);

    let effectiveStart = Math.min(startMin, currMin);
    let rawEnd = Math.max(startMin, currMin) + 30;
    let rawDuration = rawEnd - effectiveStart;

    // Regla: mínimo 90 min, de ahí en pasos de 30 min (tanto al arrastrar como al hacer clic simple)
    let finalDuration = Math.max(90, Math.ceil(rawDuration / 30) * 30);
    let finalEndMin = Math.min(END_MINUTES, effectiveStart + finalDuration);

    const horaInicio = minutesToTimeStr(effectiveStart);
    const horaFin = minutesToTimeStr(finalEndMin);

    const courtObj = dragCourt;

    // Resetear estados
    setIsDragging(false);
    setDragCourt(null);
    setDragStartSlot(null);
    setDragCurrentSlot(null);

    if (onSlotClick) {
      onSlotClick({
        cancha: courtObj,
        hora: horaInicio,
        horaInicio,
        horaFin,
        fecha: dateStr,
      });
    }
  };

  // Listener global de mouseup para evitar que quede pegado si el usuario suelta el mouse fuera de la tabla
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        finishDragSelection();
      }
    };
    window.addEventListener("mouseup", handleGlobalMouseUp);
    return () => {
      window.removeEventListener("mouseup", handleGlobalMouseUp);
    };
  }, [isDragging, dragCourt, dragStartSlot, dragCurrentSlot]);

  if (canchasDisponibles.length === 0) {
    return (
      <div className="parrilla-empty">
        <Icon icon="solar:court-broken" className="parrilla-empty-icon" />
        <p className="parrilla-empty-text">
          {sucursalSeleccionada && sucursalSeleccionada.value !== -1
            ? "No se encontraron canchas registradas para esta sucursal."
            : "Selecciona una sucursal para ver la parrilla de canchas."}
        </p>
      </div>
    );
  }

  return (
    <div className="parrilla-container">
      <div className="parrilla-table-wrapper">
        <table className="parrilla-table">
          <thead>
            <tr>
              <th className="parrilla-th-time">Hora</th>
              {canchasDisponibles.map((cancha) => (
                <th key={cancha.id} className="parrilla-th-court">
                  <div className="parrilla-court-header">
                    <span className="parrilla-court-name">{cancha.nombre}</span>
                    <span className="parrilla-court-sub">
                      {cancha.tipo || "Pádel"} {cancha.construccion ? `• ${cancha.construccion}` : ""}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {TIME_SLOTS.map((slot, index) => {
              const isHour = slot.endsWith(":00");
              return (
                <tr key={slot} className={`parrilla-row ${isHour ? "parrilla-row-hour" : "parrilla-row-half"}`}>
                  <td className="parrilla-time-label">
                    {isHour ? (
                      <span className="parrilla-time-hour">{slot}</span>
                    ) : (
                      <span className="parrilla-time-half">{slot}</span>
                    )}
                  </td>
                  {canchasDisponibles.map((cancha) => (
                    <td
                      key={cancha.id}
                      className="parrilla-cell"
                      onMouseDown={(e) => handleCellMouseDown(cancha, slot, e)}
                      onMouseEnter={() => handleCellMouseEnter(cancha, slot)}
                      title={`Reservar / Bloquear ${cancha.nombre} a las ${slot} (Arrastra para definir el tiempo)`}
                    >
                      {/* En la primera fila (06:00), renderizamos los bloques absolutos de eventos de esta cancha */}
                      {index === 0 && (
                        <div className="parrilla-events-layer">
                          {/* 1. Indicador Fantasma al Arrastrar */}
                          {currentDragRange && currentDragRange.canchaId === cancha.id && (
                            <div
                              className="parrilla-drag-selection-ghost"
                              style={{
                                top: `${currentDragRange.topPx}px`,
                                height: `${currentDragRange.heightPx}px`,
                              }}
                            >
                              <div className="parrilla-drag-selection-header">
                                <span className="parrilla-drag-selection-time">
                                  {currentDragRange.horaInicio} - {currentDragRange.horaFin}
                                </span>
                                <span className="parrilla-drag-selection-duration">
                                  {currentDragRange.duracionMinutos} min
                                </span>
                              </div>
                            </div>
                          )}

                          {/* 2. Renderizar Bloqueos */}
                          {(bloqueosPorCancha[cancha.id] || []).map((b) => {
                            const bStart = formatTimeOnly(b.horaInicio) || "07:00";
                            const bEnd = formatTimeOnly(b.horaFin) || "23:00";
                            const startMin = Math.max(START_MINUTES, timeToMinutes(bStart));
                            const endMin = Math.min(END_MINUTES, timeToMinutes(bEnd));
                            const durationMin = Math.max(15, endMin - startMin);

                            const topPx = (startMin - START_MINUTES) * PIXELS_PER_MINUTE;
                            const heightPx = durationMin * PIXELS_PER_MINUTE - 2;

                            return (
                              <div
                                key={`block-${b.id}-${cancha.id}`}
                                className="parrilla-event-block parrilla-block-bloqueo"
                                style={{
                                  top: `${topPx}px`,
                                  height: `${heightPx}px`,
                                }}
                                onMouseDown={(e) => e.stopPropagation()} // Evitar arrastre sobre un bloqueo
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (onBlockClick) onBlockClick(b, cancha);
                                }}
                                title={`Horario Bloqueado: ${b.motivo || "Sin motivo"}\n${bStart} - ${bEnd}`}
                              >
                                <div className="parrilla-event-header">
                                  <span className="parrilla-badge parrilla-badge-bloqueo">
                                    <Icon icon="solar:lock-bold" /> Bloqueado
                                  </span>
                                  <span className="parrilla-event-time">
                                    {bStart} - {bEnd}
                                  </span>
                                </div>
                                <div className="parrilla-event-title text-truncate">
                                  {b.motivo || "Horario Bloqueado"}
                                </div>
                              </div>
                            );
                          })}

                          {/* 3. Renderizar Partidas / Reservas */}
                          {(partidasPorCancha[cancha.id] || []).map((partida) => {
                            const dateObj = new Date(partida.fecha);
                            const horaInicio = dateObj.toTimeString().slice(0, 5);
                            const duracion = partida.duracion || 90;
                            const endDateObj = new Date(dateObj.getTime() + duracion * 60000);
                            const horaFin = endDateObj.toTimeString().slice(0, 5);

                            const startMin = Math.max(START_MINUTES, timeToMinutes(horaInicio));
                            const endMin = Math.min(END_MINUTES, startMin + duracion);
                            const realDuration = Math.max(15, endMin - startMin);

                            const topPx = (startMin - START_MINUTES) * PIXELS_PER_MINUTE;
                            const heightPx = realDuration * PIXELS_PER_MINUTE - 2;

                            const isApproved = partida.approvalStatus === "approved";
                            const creatorName = partida.creador
                              ? `${partida.creador.nombre || ""} ${partida.creador.apellido || ""}`.trim()
                              : "";
                            const title = partida.titulo || creatorName || `Reserva #${partida.id.slice(0, 6)}`;
                            const playersCount = (partida.jugadores || []).length;
                            const maxPlayers = partida.limiteJugadores || 4;

                            return (
                              <div
                                key={`match-${partida.id}`}
                                className={`parrilla-event-block ${
                                  isApproved ? "parrilla-block-approved" : "parrilla-block-pending"
                                }`}
                                style={{
                                  top: `${topPx}px`,
                                  height: `${heightPx}px`,
                                }}
                                onMouseDown={(e) => e.stopPropagation()} // Evitar arrastre sobre una reserva
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (onEventClick) onEventClick(partida, cancha);
                                }}
                                title={`${title}\n${horaInicio} - ${horaFin}\n${isApproved ? "Aprobada" : "Sin pagar / Pendiente"}`}
                              >
                                <div className="parrilla-event-header">
                                  <span
                                    className={`parrilla-badge ${
                                      isApproved ? "parrilla-badge-approved" : "parrilla-badge-pending"
                                    }`}
                                  >
                                    <Icon
                                      icon={isApproved ? "solar:check-circle-bold" : "solar:bill-cross-bold"}
                                    />
                                    {isApproved ? "Aprobada" : "Sin pagar"}
                                  </span>
                                  <span className="parrilla-event-time">
                                    {horaInicio} - {horaFin}
                                  </span>
                                </div>
                                <div className="parrilla-event-title text-truncate">
                                  {title}
                                </div>
                                {heightPx > 45 && (
                                  <div className="parrilla-event-footer">
                                    <span className="parrilla-event-players">
                                      <Icon icon="solar:users-group-two-rounded-bold" />
                                      {playersCount}/{maxPlayers}
                                    </span>
                                    {partida.formato && (
                                      <span className="parrilla-event-format text-truncate">
                                        {partida.formato}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
